// Generated by Copilot- Start - 2026-04-14 15:32:28
import { commonSbFixer } from "../../helper/sb-fixer";
import { logAdapterEvent, logError } from "../../helper/logger";
import { getMultiOfferDuration } from "../../../src/components/video-lab/plan-cards/utils/dynamicPrices";

const ADAPTER_NAME = "MarketingBannerAdapter";

/**
 * @description
 * Strip product-type prefix from a CMS packageId to get the Epoch-compatible key.
 * GENRE ids have no prefix; DTVI uses ATT_TV_ prefix; DTVS uses DTV_TV_ or DTV_ prefix.
 * Mirrors getEpochId() in package-carousel-adapter.
 */
const getEpochId = (productType: string, packageId: string): string => {
  switch (productType?.toUpperCase()) {
    case "DTVI":
      return packageId
        ?.replace(/^ATT_TV_/, "")
        ?.split("_")
        ?.join(" ");
    case "DTVS":
      return packageId
        ?.replace(/^DTV_TV_/, "")
        ?.replace(/^DTV_/, "")
        ?.split("_")
        ?.join(" ");
    default:
      return packageId;
  }
};

interface IMarketingBannerAdapter {
  content: any;
  index: number;
  sbPreviewAllowed?: boolean;
  modalPriorityMap?: any;
  packagesConfig?: any;
  cmsPageName?: string;
  rsnTierLegalTextObj?: any;
  genreSvodOffers?: any;
  getOffersData?: any;
  getOffersDTVsData?: any;
  packageConfig?: any[];
}

/**
 * @description
 * Resolve dynamic pricing for the Marketing Banner based on its CMS config.
 * Uses the same productType/packageId pattern as packageCarouselAdapter.
 *
 * @returns { dynamicPricing, regularPrice, promoDuration, localsPrice, noLocalsPrice } or null if no match
 */
const resolvePricing = (
  productType: string,
  packageId: string,
  genreSvodOffers: any,
  getOffersData: any,
  getOffersDTVsData: any,
  packageConfig: any[]
) => {
  try {
    let pricesReduced: any = {};
    const epochId = getEpochId(productType, packageId);

    switch (productType?.toUpperCase()) {
      case "GENRE": {
        pricesReduced =
          genreSvodOffers?.offerPrices?.reduce((acc, curr) => {
            if (!curr) {
              return acc;
            }
            Object.keys(curr || {}).forEach(key => {
              acc[key] = {
                price: curr[key]?.price ?? null,
                regularPrice: curr[key]?.regularPrice ?? null,
                promoDuration: curr[key]?.promoDuration ?? null
              };
            });
            return acc;
          }, {}) || {};
        break;
      }
      case "DTVI": {
        pricesReduced =
          getOffersData?.offerPrices?.reduce((acc, curr) => {
            if (!curr) {
              return acc;
            }
            // Each offer entry: { CHOICE: 59.99, byodPrice: {...}, benefits: [...], promosOnSelection: [...] }
            // Benefits array may contain non-discount items — apply same safety filter as plan-card-adapter.
            const validBenefits = (curr?.benefits || [])
              .filter(
                b => b &&
                  typeof b.discount === "number" &&
                  typeof b.periodMo === "number" &&
                  typeof b.code === "string"
              )
              .sort((a, b) => a.periodMo - b.periodMo);

            Object.keys(curr || {}).forEach(key => {
              // Skip metadata keys — only process actual package name keys (flat numbers)
              if (["byodPrice", "benefits", "promosOnSelection"].includes(key)) {
                return;
              }
              const price = curr[key];
              if (typeof price !== "number" && typeof price !== "string") {
                return;
              }

              // Regular (post-promo) price = promo price + sum of ALL benefit discounts
              const totalDiscount = validBenefits.reduce(
                (sum, b) => sum + Math.abs(Number(b.discount) || 0),
                0
              );
              // Max period across all benefit tiers = overall promo duration
              const maxPeriodMo = validBenefits.reduce(
                (max, b) => Math.max(max, Number(b.periodMo) || 0),
                0
              );

              acc[key] = {
                price: Number(price),
                regularPrice: totalDiscount > 0 ?
                  Number(price) + totalDiscount :
                  null,
                promoDuration: maxPeriodMo > 0 ? maxPeriodMo : null,
                // Preserve the full sorted benefits array for multi-tier placeholders
                benefits: validBenefits
              };
            });
            return acc;
          }, {}) || {};
        break;
      }
      case "DTVS": {
        // DTVS offers carry dual pricing: top-level = no-locals, localPrice = with-locals.
        // Both are stored so CMS can use [locals_price] and [no_locals_price] placeholders.
        // DTVS has no benefits array — no promo data available.
        pricesReduced =
          getOffersDTVsData?.offerPrices?.reduce((acc, curr) => {
            if (!curr) {
              return acc;
            }
            Object.keys(curr || {}).forEach(key => {
              if (key === "localPrice") {
                return;
              }
              acc[key] = {
                localsPrice: curr?.localPrice?.[key] ?? null,
                noLocalsPrice: curr?.[key] ?? null
              };
            });
            return acc;
          }, {}) || {};
        break;
      }
      default:
        return null;
    }

    const val = pricesReduced?.[epochId];
    if (!val && val !== 0) {
      // Fallback to packageConfig (uses original prefixed packageId)
      const pkgFallback = packageConfig?.find(p => p?.id === packageId);
      if (pkgFallback?.price) {
        return {
          dynamicPricing: String(pkgFallback.price),
          regularPrice: null,
          promoDuration: null,
          localsPrice: null,
          noLocalsPrice: null,
          benefits: []
        };
      }
      return null;
    }

    const priceVal =
      typeof val === "object" && val !== null ? val?.price : val;
    const regularVal =
      typeof val === "object" && val !== null ? val?.regularPrice : undefined;
    const promoDurationVal =
      typeof val === "object" && val !== null ? val?.promoDuration : undefined;

    // DTVS entries are { localsPrice, noLocalsPrice } — extract them if present
    const localsVal =
      typeof val === "object" && val !== null ? val?.localsPrice : undefined;
    const noLocalsVal =
      typeof val === "object" && val !== null ? val?.noLocalsPrice : undefined;

    // DTVI entries carry sorted benefits array for multi-tier placeholder support
    const benefitsArr =
      typeof val === "object" && val !== null && Array.isArray(val?.benefits) ?
        val.benefits :
        [];

    return {
      dynamicPricing:
        Number(priceVal) >= 0 ? String(priceVal) : null,
      regularPrice:
        regularVal !== undefined && Number(regularVal) >= 0 ?
          String(regularVal) :
          null,
      promoDuration: promoDurationVal ?? null,
      localsPrice:
        localsVal !== undefined && localsVal !== null ?
          String(localsVal) :
          null,
      noLocalsPrice:
        noLocalsVal !== undefined && noLocalsVal !== null ?
          String(noLocalsVal) :
          null,
      benefits: benefitsArr
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "resolvePricing",
      productType,
      packageId
    });
    return null;
  }
};

/**
 * @description
 * Replace placeholder tokens in a richtext content string.
 * Handles [offer_price], [regular_price], [promo_duration], [svod_names],
 * [locals_price], [no_locals_price], and multi-tier DTVI benefit placeholders:
 *   [first_offer], [first_offer_duration], [second_offer], [second_offer_duration]
 *
 * Replacements only occur if the placeholder exists in the string — no mutation otherwise.
 */
const replacePlaceholders = (
  text: string,
  pricing: {
    dynamicPricing: string | null;
    regularPrice: string | null;
    promoDuration: number | null;
    localsPrice?: string | null;
    noLocalsPrice?: string | null;
    benefits?: Array<{ discount: number; periodMo: number; code: string }>;
  },
  svodNames: string | null,
  isPromoActive: boolean,
  packageConfig?: any[],
  packageId?: string
) => {
  if (!text || typeof text !== "string") {
    return text;
  }

  let result = text;

  // [offer_price] — Epoch price → packageConfig fallback → leave unreplaced
  if (result.includes("[offer_price]")) {
    const fallbackPrice = packageConfig?.find(
      p => p?.id === packageId
    )?.price;
    const price =
      pricing?.dynamicPricing ?? (fallbackPrice ? String(fallbackPrice) : null);
    if (price !== null) {
      result = result.replace(/\[offer_price\]/g, price);
    }
  }

  // [regular_price] — only replace when promo is active; leave placeholder if no data
  if (result.includes("[regular_price]")) {
    if (isPromoActive && pricing?.regularPrice) {
      result = result.replace(/\[regular_price\]/g, pricing.regularPrice);
    }
  }

  // [promo_duration] — replace with formatted duration text; leave placeholder if no data
  if (result.includes("[promo_duration]")) {
    if (isPromoActive && pricing?.promoDuration) {
      const durationText = getMultiOfferDuration(null, pricing.promoDuration);
      result = result.replace(/\[promo_duration\]/g, durationText);
    }
  }

  // [locals_price] — DTVS with-locals price
  if (result.includes("[locals_price]")) {
    if (pricing?.localsPrice) {
      result = result.replace(/\[locals_price\]/g, pricing.localsPrice);
    }
  }

  // [no_locals_price] — DTVS no-locals price
  if (result.includes("[no_locals_price]")) {
    if (pricing?.noLocalsPrice) {
      result = result.replace(/\[no_locals_price\]/g, pricing.noLocalsPrice);
    }
  }

  // Multi-tier DTVI benefit placeholders (mirrors plan-card-adapter pattern)
  // [first_offer]          → sum of ALL benefit discounts (deepest first-month savings)
  // [first_offer_duration] → duration of first (shortest) benefit tier
  // [second_offer]         → second benefit tier discount alone
  // [second_offer_duration]→ duration range of second tier (e.g., "3-6")
  const benefits = pricing?.benefits || [];
  if (benefits.length > 0) {
    // First offer = sum of all discounts (matches plan-card-adapter firstMonthDiscount)
    if (result.includes("[first_offer]")) {
      const firstMonthDiscount = benefits.reduce(
        (sum, b) => sum + Math.abs(Number(b.discount) || 0),
        0
      );
      result = result.replace(/\[first_offer\]/g, String(firstMonthDiscount));
    }
    // First offer duration
    if (result.includes("[first_offer_duration]")) {
      const firstDuration = getMultiOfferDuration(null, benefits[0].periodMo);
      result = result.replace(/\[first_offer_duration\]/g, firstDuration);
    }
    // Second offer (only if 2+ benefit tiers)
    if (benefits.length > 1) {
      if (result.includes("[second_offer]")) {
        const secondDiscount = Math.abs(Number(benefits[1].discount) || 0);
        result = result.replace(/\[second_offer\]/g, String(secondDiscount));
      }
      if (result.includes("[second_offer_duration]")) {
        const secondDuration = getMultiOfferDuration(
          benefits[0].periodMo + 1,
          benefits[1].periodMo
        );
        result = result.replace(/\[second_offer_duration\]/g, secondDuration);
      }
    } else {
      // Single benefit — leave second-tier placeholders as-is for visibility
    }
  }
  // No benefits — leave all benefit-tier placeholders as-is for visibility

  // [svod_names] — comma-separated SVOD service names
  if (result.includes("[svod_names]") && svodNames) {
    result = result.replace(/\[svod_names\]/g, svodNames);
  }

  return result;
};

/**
 * @description
 * Replace price placeholders in a richtext field (header, body, legalText).
 * The field may arrive as an array of blocks OR as a single block object —
 * commonSbFixer's arraytoObj collapses single-element arrays into objects, so a
 * one-block header/body is an object, not an array. Normalize to a list, replace,
 * and return in the original shape so single-block fields resolve too.
 */
const processTextBlocks = (
  blocks: any,
  pricing: any,
  svodNames: string | null,
  isPromoActive: boolean,
  packageConfig?: any[],
  packageId?: string
) => {
  if (!blocks) {
    return blocks;
  }

  const blockList = Array.isArray(blocks) ? blocks : [blocks];

  const processed = blockList.map(block => {
    if (!block?.content || typeof block.content !== "string") {
      return block;
    }
    return {
      ...block,
      content: replacePlaceholders(
        block.content,
        pricing,
        svodNames,
        isPromoActive,
        packageConfig,
        packageId
      )
    };
  });

  return Array.isArray(blocks) ? processed : processed[0];
};

/**
 * @description
 * Resolve comma-separated SVOD service names for a genre package.
 * Uses genreSvodOffers.svodMapping + svodNameMapping.
 *
 * @returns "ESPN Unlimited, Vix+" or null
 */
const resolveSvodNames = (
  packageId: string,
  genreSvodOffers: any
): string | null => {
  try {
    const svodCodes = genreSvodOffers?.svodMapping?.[packageId];
    if (!Array.isArray(svodCodes) || svodCodes.length === 0) {
      return null;
    }

    const nameMapping = genreSvodOffers?.svodNameMapping || {};
    const names = svodCodes
      .map(code => nameMapping[code] || code)
      .filter(Boolean);

    return names.length > 0 ? names.join(", ") : null;
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      step: "resolveSvodNames",
      packageId
    });
    return null;
  }
};

/**
 * @description
 * Marketing Banner Adapter — conditionally injects dynamic Epoch pricing into
 * Marketing Banner CMS content. Only activates when the banner's CMS `config`
 * block specifies `productType` and `packageId`. Banners without these config
 * fields pass through with only commonSbFixer() cleanup (identical to today).
 *
 * Placeholder tokens:
 *   [offer_price]           → Epoch dynamic price (fallback: packageConfig → unreplaced)
 *   [regular_price]         → Epoch regular/list price (hidden when no promo)
 *   [promo_duration]        → Formatted promo period from Epoch benefits
 *   [svod_names]            → Included SVOD service names for the genre package
 *   [locals_price]          → DTVS with-locals price
 *   [no_locals_price]       → DTVS no-locals price
 *   [first_offer]           → DTVI first-month total discount (sum of all benefit tiers)
 *   [first_offer_duration]  → Duration of shortest benefit tier
 *   [second_offer]          → DTVI second benefit tier discount alone
 *   [second_offer_duration] → Duration range of second tier
 *
 * @params IMarketingBannerAdapter
 * @returns { props, type } — same shape as commonSbFixer
 */
export const marketingBannerAdapter = (
  args: IMarketingBannerAdapter
) => {
  try {
    const {
      content: rawContent,
      index,
      sbPreviewAllowed,
      modalPriorityMap,
      packagesConfig,
      cmsPageName,
      rsnTierLegalTextObj,
      genreSvodOffers,
      getOffersData,
      getOffersDTVsData,
      packageConfig
    } = args;

    // Step 1: Always run commonSbFixer for base cleanup + analytics
    const baseResult = commonSbFixer(
      rawContent,
      index,
      sbPreviewAllowed,
      modalPriorityMap,
      packagesConfig,
      cmsPageName,
      rsnTierLegalTextObj
    );

    if (!baseResult) {
      logAdapterEvent(ADAPTER_NAME, "commonSbFixer returned null, skipping", {
        cmsPageName
      });
      return null;
    }

    const content = baseResult.props;

    // Step 2: Guard — if no config.productType, return as-is (non-pricing banner)
    const productType = content?.config?.productType;
    const packageId = content?.config?.packageId;

    if (!productType || !packageId) {
      return baseResult;
    }

    logAdapterEvent(ADAPTER_NAME, "Dynamic pricing injection started", {
      cmsPageName,
      productType,
      packageId
    });

    // Step 3: Resolve pricing from Epoch data
    const pricing = resolvePricing(
      productType,
      packageId,
      genreSvodOffers,
      getOffersData,
      getOffersDTVsData,
      packageConfig
    );

    if (!pricing) {
      logAdapterEvent(
        ADAPTER_NAME,
        "No pricing resolved — returning base result with unmodified placeholders",
        {
          cmsPageName,
          productType,
          packageId
        }
      );
      return baseResult;
    }

    // Step 4: Determine promo state
    // For GENRE: regularPrice !== dynamicPricing indicates promo.
    // For DTVI/DTVS: promos may not set regularPrice but do set promoDuration.
    const hasPromoDuration =
      pricing.promoDuration !== null &&
      pricing.promoDuration !== undefined &&
      String(pricing.promoDuration).trim().length > 0;
    const isPromoActive =
      (pricing.regularPrice !== null &&
        pricing.dynamicPricing !== null &&
        pricing.regularPrice !== pricing.dynamicPricing) ||
      hasPromoDuration;

    // Step 5: Resolve SVOD names (GENRE productType only)
    const svodNames =
      productType?.toUpperCase() === "GENRE" ?
        resolveSvodNames(packageId, genreSvodOffers) :
        null;

    // Step 6: Replace placeholders in header, body, legalText
    const updatedContent = {
      ...content,
      ...(content.header && {
        header: processTextBlocks(
          content.header,
          pricing,
          svodNames,
          isPromoActive,
          packageConfig,
          packageId
        )
      }),
      ...(content.body && {
        body: processTextBlocks(
          content.body,
          pricing,
          svodNames,
          isPromoActive,
          packageConfig,
          packageId
        )
      }),
      ...(content.legalText && {
        legalText: processTextBlocks(
          content.legalText,
          pricing,
          svodNames,
          isPromoActive,
          packageConfig,
          packageId
        )
      })
    };

    logAdapterEvent(ADAPTER_NAME, "Dynamic pricing injection complete", {
      cmsPageName,
      productType,
      packageId,
      isPromoActive,
      offerPrice: pricing.dynamicPricing,
      regularPrice: pricing.regularPrice,
      promoDuration: pricing.promoDuration,
      localsPrice: pricing.localsPrice,
      noLocalsPrice: pricing.noLocalsPrice,
      benefitTiers: pricing.benefits?.length ?? 0,
      hasSvodNames: !!svodNames
    });

    return {
      props: updatedContent,
      type: baseResult.type
    };
  } catch (error) {
    logError(ADAPTER_NAME, error, {
      cmsPageName: args?.cmsPageName,
      step: "marketingBannerAdapter top-level"
    });
    // Fallback: return commonSbFixer result so the banner still renders
    try {
      return commonSbFixer(
        args?.content,
        args?.index,
        args?.sbPreviewAllowed,
        args?.modalPriorityMap,
        args?.packagesConfig,
        args?.cmsPageName,
        args?.rsnTierLegalTextObj
      );
    } catch {
      return null;
    }
  }
};
// Generated by Copilot- End - 2026-04-14 15:32:28

export default marketingBannerAdapter;
